<template>
  <div class="day-bars">
    <div ref="scrollRef" class="chart-scroll" @scroll.passive="active = null">
      <svg :viewBox="`0 0 ${W} ${H}`" :style="{ width: W + 'px', height: H + 'px' }" class="chart" role="group" :aria-label="ariaLabel">
        <defs>
          <pattern :id="hatchId" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width="6" height="6" fill="var(--cream)" />
            <rect width="6" height="6" :fill="color" fill-opacity=".25" />
            <line x1="0" y1="0" x2="0" y2="6" :stroke="color" stroke-opacity=".55" stroke-width="1.2" />
          </pattern>
        </defs>
        <rect v-if="band" :x="L" :y="y(band[1])" :width="W - L - R" :height="Math.max(1, y(band[0]) - y(band[1]))" fill="var(--ink)" fill-opacity="0.05" />
        <g v-for="(b, i) in buckets" :key="b.from" role="button" tabindex="0"
           :aria-label="`${b.label}: ${b.value == null ? 'записей нет' : fmt(b.value) + ' ' + unit}`"
           :aria-pressed="active === i" @click="pick(i, $event)" @keydown.enter.prevent="pick(i, $event)" @keydown.space.prevent="pick(i, $event)">
          <rect :x="xOf(i)" :y="T" :width="bw" :height="H - T - B" fill="transparent" />
          <rect v-if="b.value == null" :x="xOf(i) + gap / 2" :y="H - B - 3" :width="Math.max(2, bw - gap)" height="3" rx="1" fill="var(--ink)" fill-opacity="0.15" />
          <rect v-else :x="xOf(i) + gap / 2" :y="y(b.value)" :width="Math.max(2, bw - gap)" :height="Math.max(1, H - B - y(b.value))" rx="2"
                :fill="b.complete === false ? `url(#${hatchId})` : color" :fill-opacity="b.complete === false || active === i ? 1 : 0.85" :stroke="active === i ? 'var(--ink)' : 'none'" stroke-width="1" />
        </g>
        <line v-for="(ln, i) in lines" :key="i" :x1="L" :x2="W - R" :y1="y(ln.v)" :y2="y(ln.v)" :stroke="ln.color || 'var(--ink)'"
              :stroke-opacity="ln.color ? 1 : ln.strong ? 0.45 : 0.3" :stroke-dasharray="ln.strong ? undefined : '3 3'" stroke-width="1" pointer-events="none" />
        <text v-for="t in ticks" :key="t.i" :x="t.x" :y="H - 4" :text-anchor="t.anchor" class="lbl">{{ t.label }}</text>
      </svg>
    </div>
    <ChartDayBubble v-if="selected && activeAnchor" :key="selected.from" :anchor="activeAnchor" :label="infoLabel" :value="infoValue"
                    :action="selected.from === selected.to ? 'Открыть день' : 'По дням'"
                    @close="active = null" @open="openSelected" />
  </div>
</template>

<script setup>
import { ref, shallowRef, computed, watch, nextTick, onMounted, onUnmounted, useId } from 'vue'
import { rangeLabel, dayWord, longDate, tickIndexes } from '../utils/periodCharts.js'
import ChartDayBubble from './ChartDayBubble.vue'

const props = defineProps({
  buckets: { type: Array, required: true },
  color: { type: String, default: '#EE998C' },
  unit: { type: String, default: 'г' },
  decimals: { type: Number, default: 0 },
  lower: { type: Number, default: null },
  upper: { type: Number, default: null },
  limitColor: { type: String, default: null },
  extraLines: { type: Array, default: () => [] },
  ariaLabel: { type: String, default: 'График по дням' },
})
const emit = defineEmits(['open-day', 'zoom', 'select'])
const hatchId = 'day-hatch-' + useId()
const H = 150, L = 4, R = 6, T = 10, B = 21
const MIN_BW = 9, gap = 2
const viewportWidth = ref(320)
const W = computed(() => Math.max(viewportWidth.value, L + R + props.buckets.length * MIN_BW))
const scrollRef = ref(null)
const active = ref(null)
const activeAnchor = shallowRef(null)
let observer
function measure() { if (scrollRef.value) viewportWidth.value = scrollRef.value.clientWidth || 320 }
onMounted(() => {
  measure()
  if (typeof ResizeObserver !== 'undefined') { observer = new ResizeObserver(measure); observer.observe(scrollRef.value) }
})
onUnmounted(() => observer?.disconnect())
watch(() => props.buckets, async () => {
  active.value = null
  await nextTick()
  if (scrollRef.value) scrollRef.value.scrollLeft = scrollRef.value.scrollWidth
}, { immediate: true })
const max = computed(() => Math.max(props.upper || 0, props.lower || 0, ...props.buckets.map(b => b.value || 0), ...props.extraLines.map(l => l.v), 1) * 1.08)
const y = v => T + (H - T - B) * (1 - Math.min(v, max.value) / max.value)
const bw = computed(() => (W.value - L - R) / Math.max(1, props.buckets.length))
const xOf = i => L + i * bw.value
const band = computed(() => props.lower != null && props.upper != null ? [props.lower, props.upper] : null)
const lines = computed(() => [
  ...(props.upper != null ? [{ v: props.upper, strong: props.lower == null, color: props.limitColor }] : []),
  ...(props.lower != null ? [{ v: props.lower }] : []),
  ...props.extraLines,
])
const ticks = computed(() => tickIndexes(props.buckets.map(b => b.from)).map(i => {
  const cx = xOf(i) + bw.value / 2
  const anchor = cx < 42 ? 'start' : cx > W.value - 42 ? 'end' : 'middle'
  return { i, label: longDate(props.buckets[i].from), anchor, x: anchor === 'start' ? xOf(i) : anchor === 'end' ? Math.min(W.value - R, cx + bw.value / 2) : cx }
}))
const selected = computed(() => active.value == null ? null : props.buckets[active.value])
const weekday = new Intl.DateTimeFormat('ru-RU', { weekday: 'long', timeZone: 'UTC' })
const infoLabel = computed(() => {
  const b = selected.value
  if (!b) return ''
  return b.from === b.to
    ? `${longDate(b.from)}, ${weekday.format(new Date(b.from + 'T12:00:00Z'))}`
    : rangeLabel(b.from, b.to)
})
const infoValue = computed(() => {
  const b = selected.value
  if (!b || b.value == null) return 'Записей нет'
  return fmt(b.value) + ' ' + props.unit + (b.n > 1 ? ` в среднем за ${b.n} ${dayWord(b.n)}` : '')
})
function pick(i, event) {
  activeAnchor.value = event.currentTarget
  active.value = active.value === i ? null : i
  if (active.value != null) emit('select', props.buckets[i])
}
function openSelected() {
  const b = selected.value
  if (!b) return
  if (b.from === b.to) emit('open-day', b.from)
  else emit('zoom', { from: b.from, to: b.to })
  active.value = null
}
function fmt(v) { return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: props.decimals }).format(v) }
</script>

<style scoped>
.chart-scroll { overflow-x: auto; overscroll-behavior-x: contain; scrollbar-width: none; }
.chart-scroll::-webkit-scrollbar { display: none; }
.chart { display: block; max-width: none; }
.chart g { cursor: pointer; }
.chart g:focus-visible { outline: 1px solid var(--ink); outline-offset: -1px; }
.lbl { font-family: Inter, sans-serif; font-size: var(--fs-12); fill: var(--ink-70); }
</style>
